import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, OperatorFunction, catchError, filter, map, throwError, timeout } from 'rxjs';

/**
 * Espera maxima SIN AVANCE de una subida o descarga. Antes no habia ninguna: con la
 * conexion de la tienda caida el pedido quedaba colgado para siempre y la pantalla
 * detras ("Emitiendo contrato, por favor espere") no se cerraba nunca (Prod B,
 * 2026-09-30). Se mide la inactividad y no el tiempo total: las tiendas mas lentas
 * suben y bajan a unos 50 KB/s, a veces tres soportes en paralelo, y un archivo
 * grande que sigue avanzando no se debe cortar solo por tardar.
 */
const ESPERA_SIN_AVANCE_MS = 90000;

@Injectable({
  providedIn: 'root'
})
export class FileManagerService {

  private http = inject(HttpClient);
  private URL_API = `${environment.API_URL}/files`;

  uploadFile(file: File): Observable<any> {
    const base = this.URL_API.replace(/\/$/, '');
    const url = `${base}/upload`;
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(url, formData, { observe: 'events', reportProgress: true }).pipe(
      this.cortarSinAvance<any>(url, 'La subida del archivo se detuvo. Revise su conexión e intente de nuevo.'),
      catchError(this.handlerError)
    );
  }

  downloadFile(id: string): Observable<Blob> {
    const base = this.URL_API.replace(/\/$/, '');
    const url = `${base}/download/${id}`;
    return this.http.post(url, {}, { responseType: 'blob', observe: 'events', reportProgress: true }).pipe(
      this.cortarSinAvance<Blob>(url, 'La descarga del archivo se detuvo. Revise su conexión e intente de nuevo.')
    );
  }

  deleteFile(id: string): Observable<any> {
    const base = this.URL_API.replace(/\/$/, '');
    const url = `${base}/${id}`;
    return this.http.delete(url).pipe(catchError(this.handlerError));
  }

  /**
   * Corta el pedido si pasan ESPERA_SIN_AVANCE_MS sin ningun evento (progreso de subida
   * o de bajada, o la respuesta) y entrega solo el cuerpo de la respuesta final, como
   * si el pedido no observara eventos.
   */
  private cortarSinAvance<T>(url: string, message: string): OperatorFunction<HttpEvent<T>, T> {
    return (eventos$) => eventos$.pipe(
      timeout({
        each: ESPERA_SIN_AVANCE_MS,
        with: () => throwError(() => this.errorDeTiempo(url, message)),
      }),
      filter((evento): evento is HttpResponse<T> => evento.type === HttpEventType.Response),
      map((respuesta) => respuesta.body as T)
    );
  }

  /**
   * Error con la misma forma que uno de la API (status 0, como una conexion cortada, y
   * el motivo en error.message), para que las pantallas que leen err.error.message
   * muestren el motivo sin cambios.
   */
  private errorDeTiempo(url: string, message: string): HttpErrorResponse {
    return new HttpErrorResponse({ status: 0, statusText: 'Timeout', url, error: { message } });
  }

  private handlerError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }
}
